import React, { Fragment, useMemo, useState } from 'react';
import { prop } from 'ramda';
import { FormSpy } from 'react-final-form';
import Form from 'js/components/common/Form';
import { useFormsSelector } from 'js/store/hooks';
import { Loader } from 'js/components/common/Loader';
import { ReCaptcha } from 'js/components/main/ReCaptcha';
import { useComponentTranslations } from 'js/hooks/component';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';
import { cls } from 'js/utils/classNameBuilder.util';

import * as hooks from './hooks';
import * as helpers from './helpers';
import { ChangeFormContext } from './store/context';
import { FormRulesSpy } from './components/FormRulesSpy';
import { ChangeFormField, useRecaptchaValidation } from './components/ChangeFormField';

import style from './ChangeForm.scss';

export const ChangeForm = ({
    formName,
    onSubmit,
    /** @type Object */
    startingValues = null,
    formError = '',
    validationOperation = '',
    submitTags = true,
    /** @type Object */
    classNames = {}
}) => {
    const translate = useComponentTranslations();
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [valuesOnSubmit, setValuesOnSubmit] = useState({});
    const formConfig = hooks.useForm({ formName });
    const fields = hooks.useFields({ formConfig });
    const rules = hooks.useRules({ formConfig });
    const [playerTags, fetchPlayerTags] = hooks.usePlayerTags({ fields, setIsFormLoading });
    const initialValues = hooks.useInitialValues({ fields, playerTags });
    const defaultValues = hooks.useDefaultValues({ fields });
    const isFormsLoaded = useFormsSelector(prop('isFormsLoaded'));
    const buildCls = cls([style, classNames]);

    const combinedInitialValues = useMemo(() => ({
        ...startingValues,
        ...defaultValues,
        /**
         * valuesOnSubmit - is needed to don't clear and validate sensitive fields after submit
         * sensitive fields - can be considered as fields with imsField which aren't included in userDetails,
         * e.g. oldPassword, newPassword, currentPin, newAuthenticationPhonePin
         */
        ...valuesOnSubmit,
        ...initialValues
    }), [startingValues, initialValues, defaultValues, valuesOnSubmit]);

    const [hiddenByRulesFields, setHiddenByRulesFields] = useState(
        helpers.getHiddenByRulesFields({ rules, values: combinedInitialValues })
    );
    const validationConfig = hooks.useValidationConfig({ fields, hiddenByRulesFields });

    const initialSubmitButton = fields
        .find((field) => field.type === FORM_FIELD_TYPE.BUTTON && field.action === 'submit') ?? null;
    const [submitButton, setSubmitButton] = useState(initialSubmitButton);

    const {
        isReCaptchaVisible,
        onReCaptchaTokenValidate,
        onReCaptchaTokenError,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    } = useRecaptchaValidation();

    const onCombinedSubmit = hooks.useOnCombinedSubmit({
        fields,
        onSubmit,
        submitTags,
        playerTags,
        fetchPlayerTags,
        setValuesOnSubmit,
        hiddenByRulesFields,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    });

    if (!isFormsLoaded) {
        return <Loader />;
    }

    return (
        <Form
            key={formName}
            onSubmit={onCombinedSubmit}
            validationConfig={validationConfig}
            initialValues={combinedInitialValues}
            render={({ handleSubmit, submitting }) => (
                <form
                    onSubmit={event => event.preventDefault()}
                    data-form-name={formName}
                    className={`${formConfig ? buildCls(['form']) : null} cms-dynamic-form cms-${formName}`}
                >
                    {formConfig ? null : (
                        <ErrorMessage
                            className={buildCls(['error'])}
                            error={translate('playtech.mobile.system.change-form.error.incorrect-form', [formName])}
                        />)
                    }
                    <FormSpy
                        subscription={{ submitError: true }}
                        render={({ submitError }) => submitError || formError ?
                            <ErrorMessage error={submitError || formError} animate={true} />
                            : null
                        }>
                    </FormSpy>
                    {rules.length > 0 && (
                        <FormSpy
                            subscription={{ active: true, values: true }}
                            render={({ active, values }) => (
                                <FormRulesSpy
                                    rules={rules}
                                    fields={fields}
                                    active={active}
                                    values={values}
                                    hiddenByRulesFields={hiddenByRulesFields}
                                    setHiddenByRulesFields={setHiddenByRulesFields}
                                />
                            )}
                        />
                    )}
                    {fields.map((field) => {
                        const isFieldHidden = hiddenByRulesFields.includes(field.name);

                        return (
                            <Fragment key={field.name}>
                                {!isFieldHidden && isReCaptchaVisible && submitButton === field.name ? (
                                    <div className={buildCls(['captcha'])}>
                                        <ReCaptcha
                                            operation={validationOperation}
                                            onValidate={onReCaptchaTokenValidate}
                                            onError={onReCaptchaTokenError}
                                        />
                                    </div>
                                ) : null}
                                <ChangeFormContext.Provider value={{ formName }}>
                                    <ChangeFormField
                                        field={field}
                                        formID={formConfig.id}
                                        handleSubmit={handleSubmit}
                                        isFieldHidden={isFieldHidden}
                                        setSubmitButton={setSubmitButton}
                                        disabled={submitting || isFormLoading}
                                        validationConfig={validationConfig[field.name]}
                                    />
                                </ChangeFormContext.Provider>
                            </Fragment>
                        );
                    })}
                    {isReCaptchaVisible && submitButton === null ? (
                        <div className={buildCls(['captcha'])}>
                            <ReCaptcha
                                operation={validationOperation}
                                onValidate={onReCaptchaTokenValidate}
                                onError={onReCaptchaTokenError}
                            />
                        </div>
                    ) : null}
                </form>
            )}
        />
    );
};
